import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { FileXls, MagnifyingGlass, Check, SpinnerGap, Download, ForkKnife, Basket, Plus, Minus } from "@phosphor-icons/react";
import { toast } from "sonner";
import { downloadBase64File } from "@/utils/downloadHelper";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ShoppingListBuilderDialog({ open, onClose }) {
  const [sheets, setSheets] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [selectedSheets, setSelectedSheets] = useState({});   // {sheet_id: qty}
  const [selectedIngs, setSelectedIngs] = useState({});       // {ingredient_id: qty}
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState("sheets");
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSelectedSheets({});
    setSelectedIngs({});
    setSearch("");
    setTab("sheets");
    setLoading(true);
    (async () => {
      try {
        const [s, i] = await Promise.all([
          axios.get(`${API}/technical-sheets`),
          axios.get(`${API}/ingredients`),
        ]);
        setSheets(s.data || []);
        setIngredients(i.data || []);
      } catch {
        toast.error("Erro ao carregar fichas e ingredientes");
      }
      setLoading(false);
    })();
  }, [open]);

  const filteredSheets = useMemo(() => {
    const q = search.toLowerCase();
    return sheets.filter((s) => (s.name || "").toLowerCase().includes(q) || (s.category || "").toLowerCase().includes(q));
  }, [sheets, search]);

  const filteredIngs = useMemo(() => {
    const q = search.toLowerCase();
    return ingredients.filter((i) => (i.name || "").toLowerCase().includes(q) || (i.category || "").toLowerCase().includes(q));
  }, [ingredients, search]);

  const toggleSheet = (id) => {
    setSelectedSheets((prev) => {
      const copy = { ...prev };
      if (copy[id]) delete copy[id]; else copy[id] = 1;
      return copy;
    });
  };
  const bumpSheet = (id, delta) => {
    setSelectedSheets((prev) => {
      const cur = prev[id] || 1;
      const next = Math.max(1, cur + delta);
      return { ...prev, [id]: next };
    });
  };
  const toggleIng = (id) => {
    setSelectedIngs((prev) => {
      const copy = { ...prev };
      if (copy[id]) delete copy[id]; else copy[id] = 1;
      return copy;
    });
  };
  const bumpIng = (id, delta) => {
    setSelectedIngs((prev) => {
      const cur = prev[id] || 1;
      const next = Math.max(0.1, +(cur + delta).toFixed(2));
      return { ...prev, [id]: next };
    });
  };

  const sheetCount = Object.keys(selectedSheets).length;
  const ingCount = Object.keys(selectedIngs).length;
  const totalCount = sheetCount + ingCount;

  const handleGenerate = async () => {
    if (totalCount === 0) {
      toast.error("Selecione ao menos uma ficha ou ingrediente");
      return;
    }
    setExporting(true);
    try {
      const payload = {
        sheets: Object.entries(selectedSheets).map(([sheet_id, quantity]) => ({ sheet_id, quantity })),
        extra_ingredients: Object.entries(selectedIngs).map(([ingredient_id, quantity]) => ({ ingredient_id, quantity })),
      };
      const { data } = await axios.post(`${API}/export/shopping-from-sheets`, payload);
      downloadBase64File(
        data.file_base64,
        data.file_name,
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      );
      toast.success(`Lista gerada com ${data.items_count} ingredientes (R$ ${data.grand_total})`);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao gerar lista");
    }
    setExporting(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[620px] max-h-[88vh] overflow-hidden flex flex-col" data-testid="shopping-builder-dialog">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold flex items-center gap-2">
            <FileXls size={20} className="text-emerald-600" />
            Gerar Lista de Compras
          </DialogTitle>
          <DialogDescription className="text-xs text-zinc-500">
            Selecione fichas técnicas e/ou ingredientes avulsos. A lista sai no seu modelo de Excel.
          </DialogDescription>
        </DialogHeader>

        {/* Search */}
        <div className="relative">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input
            placeholder={tab === "sheets" ? "Buscar fichas..." : "Buscar ingredientes..."}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 h-9 text-sm"
            data-testid="shopping-builder-search"
          />
        </div>

        <Tabs value={tab} onValueChange={setTab} className="flex flex-col flex-1 overflow-hidden">
          <TabsList className="grid grid-cols-2">
            <TabsTrigger value="sheets" data-testid="tab-sheets" className="text-sm">
              <ForkKnife size={14} className="mr-1.5" /> Fichas {sheetCount > 0 ? `(${sheetCount})` : ""}
            </TabsTrigger>
            <TabsTrigger value="ings" data-testid="tab-ings" className="text-sm">
              <Basket size={14} className="mr-1.5" /> Avulsos {ingCount > 0 ? `(${ingCount})` : ""}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="sheets" className="flex-1 overflow-y-auto mt-3 border rounded-lg divide-y divide-zinc-100" data-testid="sheets-list">
            {loading ? (
              <div className="flex items-center justify-center py-10">
                <SpinnerGap size={24} className="text-zinc-300 animate-spin" />
              </div>
            ) : filteredSheets.length === 0 ? (
              <div className="text-center py-8 text-zinc-400 text-sm">
                {search ? "Nenhuma ficha encontrada" : "Nenhuma ficha cadastrada"}
              </div>
            ) : (
              filteredSheets.map((s) => {
                const qty = selectedSheets[s.id];
                const isSelected = !!qty;
                return (
                  <div key={s.id} className={`flex items-center gap-3 px-3 py-2.5 text-left transition-colors ${isSelected ? "bg-emerald-50" : "hover:bg-zinc-50"}`} data-testid={`sheet-row-${s.id}`}>
                    <button onClick={() => toggleSheet(s.id)} className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${isSelected ? "bg-emerald-600 border-emerald-600" : "border-zinc-300"}`}>
                      {isSelected && <Check size={12} weight="bold" className="text-white" />}
                    </button>
                    <button onClick={() => toggleSheet(s.id)} className="flex-1 min-w-0 text-left">
                      <p className="text-sm font-medium text-zinc-900 truncate">{s.name}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        {s.yield_quantity && (
                          <span className="text-[10px] text-zinc-400">Rend: {s.yield_quantity} {s.yield_unit}</span>
                        )}
                        {s.total_cost > 0 && (
                          <span className="text-[10px] text-emerald-600">R$ {Number(s.total_cost).toFixed(2)}</span>
                        )}
                        {s.ingredients && (
                          <span className="text-[10px] text-zinc-400">{s.ingredients.length} ingr.</span>
                        )}
                      </div>
                    </button>
                    {isSelected && (
                      <div className="flex items-center gap-1 flex-shrink-0" data-testid={`sheet-qty-${s.id}`}>
                        <button onClick={() => bumpSheet(s.id, -1)} className="w-6 h-6 rounded border border-zinc-200 flex items-center justify-center hover:bg-zinc-100">
                          <Minus size={12} />
                        </button>
                        <span className="text-xs font-semibold w-8 text-center">{qty}x</span>
                        <button onClick={() => bumpSheet(s.id, 1)} className="w-6 h-6 rounded border border-zinc-200 flex items-center justify-center hover:bg-zinc-100">
                          <Plus size={12} />
                        </button>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </TabsContent>

          <TabsContent value="ings" className="flex-1 overflow-y-auto mt-3 border rounded-lg divide-y divide-zinc-100" data-testid="ings-list">
            {loading ? (
              <div className="flex items-center justify-center py-10">
                <SpinnerGap size={24} className="text-zinc-300 animate-spin" />
              </div>
            ) : filteredIngs.length === 0 ? (
              <div className="text-center py-8 text-zinc-400 text-sm">
                {search ? "Nenhum ingrediente encontrado" : "Nenhum ingrediente cadastrado"}
              </div>
            ) : (
              filteredIngs.map((i) => {
                const qty = selectedIngs[i.id];
                const isSelected = qty !== undefined;
                return (
                  <div key={i.id} className={`flex items-center gap-3 px-3 py-2.5 text-left transition-colors ${isSelected ? "bg-emerald-50" : "hover:bg-zinc-50"}`} data-testid={`ing-row-${i.id}`}>
                    <button onClick={() => toggleIng(i.id)} className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${isSelected ? "bg-emerald-600 border-emerald-600" : "border-zinc-300"}`}>
                      {isSelected && <Check size={12} weight="bold" className="text-white" />}
                    </button>
                    <button onClick={() => toggleIng(i.id)} className="flex-1 min-w-0 text-left">
                      <p className="text-sm font-medium text-zinc-900 truncate">{i.name}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-[10px] text-zinc-400">{i.unit}</span>
                        {i.purchase_price > 0 && (
                          <span className="text-[10px] text-emerald-600">R$ {Number(i.purchase_price).toFixed(2)}</span>
                        )}
                        {i.category && (
                          <span className="text-[10px] bg-zinc-100 text-zinc-500 px-1.5 py-0.5 rounded">{i.category}</span>
                        )}
                      </div>
                    </button>
                    {isSelected && (
                      <div className="flex items-center gap-1 flex-shrink-0" data-testid={`ing-qty-${i.id}`}>
                        <button onClick={() => bumpIng(i.id, -1)} className="w-6 h-6 rounded border border-zinc-200 flex items-center justify-center hover:bg-zinc-100">
                          <Minus size={12} />
                        </button>
                        <Input
                          type="number"
                          value={qty}
                          onChange={(e) => {
                            const v = parseFloat(e.target.value);
                            if (!isNaN(v) && v >= 0.1) {
                              setSelectedIngs((prev) => ({ ...prev, [i.id]: v }));
                            }
                          }}
                          className="w-14 h-6 text-xs text-center p-1"
                          step="0.1"
                          min="0.1"
                        />
                        <span className="text-[10px] text-zinc-400">{i.unit}</span>
                        <button onClick={() => bumpIng(i.id, 1)} className="w-6 h-6 rounded border border-zinc-200 flex items-center justify-center hover:bg-zinc-100">
                          <Plus size={12} />
                        </button>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </TabsContent>
        </Tabs>

        {/* Footer */}
        <div className="flex justify-between items-center pt-2 border-t border-zinc-100">
          <div className="text-xs text-zinc-500">
            {totalCount === 0 ? "Nenhum item selecionado" : `${sheetCount} ficha${sheetCount !== 1 ? "s" : ""} + ${ingCount} avulso${ingCount !== 1 ? "s" : ""}`}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={onClose} data-testid="shopping-builder-cancel">
              Cancelar
            </Button>
            <Button
              onClick={handleGenerate}
              disabled={totalCount === 0 || exporting}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="shopping-builder-generate"
            >
              {exporting ? (
                <SpinnerGap size={16} className="mr-2 animate-spin" />
              ) : (
                <Download size={16} className="mr-2" />
              )}
              Gerar Lista {totalCount > 0 ? `(${totalCount})` : ""}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
